import { createClient } from '@supabase/supabase-js';

// Add verbose debug logging
console.log('Initializing Supabase client...');

if (!import.meta.env.VITE_SUPABASE_URL) {
  console.error('ERROR: Missing VITE_SUPABASE_URL environment variable');
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
  console.error('ERROR: Missing VITE_SUPABASE_ANON_KEY environment variable');
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('Supabase Configuration:', {
  url: supabaseUrl,
  hasAnonKey: !!supabaseAnonKey
});

// Define UserProfile type
export interface UserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  updated_at?: string;
  created_at?: string;
  terms_accepted?: boolean;
  terms_accepted_at?: string;
  password_exists?: boolean;
  password_set?: boolean;
  [key: string]: any; // For any additional fields
}

// Cache for API responses
type CacheEntry = {
  data: any;
  timestamp: number;
  expiresIn: number;
}

// Global request cache with expiration
const apiCache: Record<string, CacheEntry> = {};

// Function to cache API responses
export const cachedFetch = async (url: string, options?: RequestInit, ttlMs: number = 60000) => {
  const cacheKey = `${url}:${JSON.stringify(options || {})}`;
  
  // Check if we have a valid cached response
  const cachedResponse = apiCache[cacheKey];
  const now = Date.now();
  
  if (cachedResponse && (now - cachedResponse.timestamp < cachedResponse.expiresIn)) {
    console.log(`Using cached response for ${url}`);
    return cachedResponse.data;
  }
  
  console.log(`Fetching fresh data from ${url}`);
  const response = await fetch(url, options);
  
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`);
  }
  
  const data = await response.json();
  
  // Cache the response
  apiCache[cacheKey] = {
    data,
    timestamp: now,
    expiresIn: ttlMs
  };
  
  return data;
};

// Create Supabase client with default options
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Clear all Supabase data from localStorage
export const clearSupabaseData = () => {
  try {
    console.log('Clearing all Supabase data from localStorage');
    // Remove all keys that start with 'sb-'
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) {
        localStorage.removeItem(key);
      }
    });
    return true;
  } catch (error) {
    console.error('Error clearing Supabase data:', error);
    return false;
  }
};

// Force logout by clearing session data
export const forceLogout = async () => {
  try {
    // Make a direct API call to /auth/v1/logout
    const response = await fetch(`${supabaseUrl}/auth/v1/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey
      },
      credentials: 'include'
    });
    
    // Clear local data regardless of API success
    clearSupabaseData();
    
    return response.ok;
  } catch (error) {
    console.error('Error during force logout:', error);
    // Still clear local data even if API call fails
    clearSupabaseData();
    return false;
  }
};

// Cached version of the auth user endpoint
export const getCachedAuthUser = async (headers?: HeadersInit) => {
  const url = `${supabaseUrl}/auth/v1/user`;
  
  try {
    const authToken = supabase.auth.getSession().then(({ data }) => data.session?.access_token);
    
    if (!authToken) {
      console.log('No auth token available for getCachedAuthUser');
      return null;
    }
    
    return await cachedFetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${await authToken}`,
        'apikey': supabaseAnonKey,
        ...headers
      }
    }, 30000); // 30 second cache
  } catch (error) {
    console.error('Error in getCachedAuthUser:', error);
    return null;
  }
};

// Log when key methods are called
const originalGetSession = supabase.auth.getSession;
supabase.auth.getSession = async function() {
  // console.log('Debug: supabase.auth.getSession called');
  try {
    const result = await originalGetSession.call(this);
    // console.log('Debug: getSession result:', { 
    //   hasSession: !!result.data.session,
    //   hasUser: !!result.data.session?.user,
    //   error: result.error
    // });
    return result;
  } catch (error) {
    console.error('Debug: getSession error:', error);
    throw error;
  }
};

const originalGetUser = supabase.auth.getUser;
supabase.auth.getUser = async function(jwt?: string) {
  console.log('Debug: supabase.auth.getUser called');
  try {
    const result = await originalGetUser.call(this, jwt);
    console.log('Debug: getUser result:', { 
      hasUser: !!result.data.user,
      error: result.error
    });
    return result;
  } catch (error) {
    console.error('Debug: getUser error:', error);
    throw error;
  }
};

// Add specific method to get user directly
export const getCurrentUser = async () => {
  console.log('Explicit getCurrentUser call');
  try {
    // First try JWT session
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      console.log('User found in JWT:', user.id);
      return user;
    }
    
    // If no user from JWT, try session storage
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      console.log('User found in session:', session.user.id);
      return session.user;
    }
    
    console.log('No user found in JWT or session');
    return null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Utility to check if a user has permission to access premium endpoints
export const canAccessPremiumEndpoint = async (userId: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    // Check the cache first
    const cacheKey = `premium_access:${userId}`;
    const cachedValue = localStorage.getItem(cacheKey);
    
    if (cachedValue) {
      const { hasAccess, expires } = JSON.parse(cachedValue);
      // If cache is still valid, use it
      if (expires > Date.now()) {
        return hasAccess;
      }
    }
    
    // Otherwise query the database
    const { data, error } = await supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error('Error checking premium access:', error);
      return false;
    }
    
    const hasAccess = data.role === 'pro' || data.role === 'pro_yearly';
    
    // Cache the result for 5 minutes
    localStorage.setItem(cacheKey, JSON.stringify({
      hasAccess,
      expires: Date.now() + 5 * 60 * 1000
    }));
    
    return hasAccess;
  } catch (error) {
    console.error('Error checking premium endpoint access:', error);
    return false;
  }
};

// Clear API cache when user logs out
export const clearApiCache = () => {
  Object.keys(apiCache).forEach(key => delete apiCache[key]);
  
  // Also clear premium access cache entries
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('premium_access:')) {
      localStorage.removeItem(key);
    }
  });
  
  console.log('API cache cleared');
};

console.log('Supabase client setup complete'); 